import React, {useMemo} from 'react';
import {Dialogue, LoadingSpinner, Modal} from '@albert-io/atomic';

import {useAdminContentLocks} from './useAdminContentLocks';
import {isEffectivelyLocked} from './contentLockDerivation';
import type {GuideTree} from './contentLockDerivation';
import {RestrictionForm} from './RestrictionForm';

interface ManageRestrictionsModalProps {
  guideLevelName: string;
  /** Guide-level id — the lock source for seeding, impact preview, and the lock/unlock mutations. */
  guideLevelId?: string;
  /**
   * The guide's level tree. When provided, toggles seed from full effective-lock state and Confirm
   * performs the parent-child decomposition; without it, the modal acts on this single level only.
   */
  guideLevelTree?: GuideTree;
  onClose: () => void;
}

/**
 * Admin "Manage Restrictions" modal (admin-locked-assessments, Parts 1–2). Loads the admin's
 * per-school lock state, then renders {@link RestrictionForm} (mounted only once it resolves, so the
 * form's toggles seed synchronously).
 */
export const ManageRestrictionsModal = ({
  guideLevelName,
  guideLevelId,
  guideLevelTree,
  onClose
}: ManageRestrictionsModalProps) => {
  const {schools, loading, getBreakdown, getBreakdownWhere, getLockedSourceIds, getLockId} =
    useAdminContentLocks();

  // Seed each school's toggle from its effective lock state (with the tree: self / ancestor /
  // all-children; without it: a direct lock). Memoized so unrelated re-renders don't re-walk the tree.
  const initialRestrictedIds = useMemo(() => {
    if (!guideLevelId) {
      return [];
    }
    const breakdown = guideLevelTree
      ? getBreakdownWhere((lockedSourceIds) =>
          isEffectivelyLocked(guideLevelTree, guideLevelId, lockedSourceIds)
        )
      : getBreakdown(guideLevelId);
    return breakdown.restricted.map((school) => school.id);
  }, [guideLevelId, guideLevelTree, getBreakdown, getBreakdownWhere]);

  return (
    <Modal ariaLabel='Manage Restrictions for' handleClose={onClose}>
      {() => (
        <Dialogue
          inModal
          size='l'
          title='Manage Restrictions for'
          handleClose={onClose}
          className='[&_.o-dialogue\_\_title]:mb-x1'
        >
          {loading ? (
            <Dialogue.Body>
              <div className='py-x4 flex justify-center'>
                <LoadingSpinner />
              </div>
            </Dialogue.Body>
          ) : (
            <RestrictionForm
              guideLevelName={guideLevelName}
              guideLevelId={guideLevelId}
              guideLevelTree={guideLevelTree}
              schools={schools}
              initialRestrictedIds={initialRestrictedIds}
              getLockedSourceIds={getLockedSourceIds}
              getLockId={getLockId}
              onClose={onClose}
            />
          )}
        </Dialogue>
      )}
    </Modal>
  );
};

export default ManageRestrictionsModal;
